import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { Modal as SemanticModal } from "semantic-ui-react"; // todo: can we use our modal?

import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import Radio from "@cayuse/shared-react.components.form.inputs.radio";
import Modal from "@cayuse/shared-react.components.modal";
import { isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import SearchBar from "components/SearchBar";

import {
	COLUMN_HEADER_KEYS,
	ERROR_NOT_AUTHORIZED,
	ERROR_STATUS_CODE_400,
	PRIVILEGES,
	START_AWARD_FROM_AWARD,
	START_PROPOSAL_FROM_PROJECT,
	SUBAWARDS_BASE_ROUTE,
} from "globals/constants";
import { useHasPrivileges, useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";
import {
	createFromOptions,
	debounce,
	notifyError,
	notifySuccess,
	spUrl,
	validateTitleLength,
	trapFocusInsideNotification
} from "utils";
import history from "utils/history";

import styles from "./styles.css";
import { api } from "services";
import { IncompatibleWarning } from "../FormResponse/IncompatibleWarning";
import { toast } from "react-toastify";
import { fetchShell } from "../../store/actions/shell";

const MODAL_ID = "newSubawardModal";

const actions = {
	create: "create",
	addToProject: "add to project",
	addToAward: "add to award"
};

const defaults = {
	action: undefined,
	visible: false,
	open: false,
	transitionDuration: 150,
	titleField: "",
	searchField: undefined,
	searchInput: "",
	confirmDisabled: true,
	searchQuery: ""
};

const StartNewSubaward = ({ fromProject = false, projectHasAwards = true, projectId = null }) => {
	const dictionary = useDictionary("subawards.startNewSubaward");

	const [ isLoading, setIsLoading ] = useState(false);
	const [ action, setAction ] = useState(defaults.action);
	const [ visible, setVisible ] = useState(defaults.visible);
	const [ open, setOpen ] = useState(defaults.open);
	const [ titleField, setTitleField ] = useState(defaults.titleField);
	const [ searchField, setSearchField ] = useState(defaults.searchField);
	const [ searchInput, setSearchInput ] = useState(defaults.searchInput);
	const [ confirmDisabled, setConfirmDisabled ] = useState( defaults.confirmDisabled);
	const [ disabledReason, setDisabledReason ] = useState(null);
	const [ projectOptions, setProjectOptions ] = useState([]);
	const [ awardOptions, setAwardOptions ] = useState([]);
	const [ isFetchingOptions, setIsFetchingOptions ] = useState(false);

	const { tenantId } = useSelector(tenantSelectors.tenant);

	const { [PRIVILEGES.SUBAWARDS_CREATE]: canCreateSubawards } = useHasPrivileges([ PRIVILEGES.SUBAWARDS_CREATE ]);

	const ariaControlsAttr = open ? { [`aria-controls`]: MODAL_ID } : {};

	useEffect(() => {
		const errors = dictionary.errors || {};
		let reason = null;

		if (confirmDisabled) {
			if (!action) {
				reason = errors.noActionSelected || "Please select where to create a new subaward";
			} else if (action === actions.create) {
				reason = errors.noProjectTitle || "Please enter a project title";
			} else if (action === actions.addToProject && !fromProject) {
				reason = errors.noProjectSelected || "Please select a project";
			}  else if (action === actions.addToAward) {
				reason = errors.noAwardSelected || "Please select an award";
			}
		}

		if (reason !== disabledReason) {
			setDisabledReason(reason);
		}
	}, [
		action,
		actions.create,
		confirmDisabled,
		dictionary.errors,
		disabledReason,
		searchField,
		titleField,
	]);

	const onActionChange = value => {
		setSearchInput(defaults.searchInput);
		setTitleField(defaults.titleField);
		setAction(value);
		if (value === actions.addToProject && fromProject ) {
			setConfirmDisabled(false);
		} else {
			setConfirmDisabled(true);
		}

		if (!visible) {
			setVisible(true);
		}
	};

	const addToManyResultsText = useCallback((options, totalElements) => {
		if(options?.length < totalElements) {
			return {
				key: "refine-results",
				value: "refine-results",
				text: `There are too many results for "${searchInput}", try adding to your search to see more specific results`,
				disabled: true
			};
		}

		return false;
	}, [ searchInput ]);

	const loadProjectOptions = () => {
		if(isNonEmptyString(searchInput) && action === actions.addToProject) {
			createFromOptions({
				fields: START_PROPOSAL_FROM_PROJECT.requiredFields,
				searchFields: START_PROPOSAL_FROM_PROJECT.searchFields,
				searchQuery: searchInput,
				formType: COLUMN_HEADER_KEYS.PROJECT.toLowerCase(),
			})
				.then(results => {
					const updatedOptions = results?.content?.map((projectItem, index) => {
						const textString = `${projectItem.projectNumber} : ${projectItem.title}`;

						if(isNonEmptyString(projectItem.projectNumber) && isNonEmptyString(projectItem.title)) {
							return {
								content: textString,
								key: `${projectItem.projectId}`,
								value: projectItem.projectId,
								original: textString,
								text: `Result ${index + 1} of ${results?.content?.length} for ${searchInput}: ${textString}`,
								projectTitle: projectItem.title,
							};
						}

						return { key: "undefined" };
					});

					const toManyResultsText = addToManyResultsText(updatedOptions, results?.totalElements, searchInput);

					if(toManyResultsText) {
						updatedOptions.push(toManyResultsText);
					}

					// Remove duplicate projects
					const ids = updatedOptions.map(option => option.key);
					const filteredOptions = updatedOptions.filter(({ key }, index) => (
						!ids.includes(key, index + 1) && key !== "undefined"
					));

					setProjectOptions(filteredOptions);
				})
				.finally(() => setIsLoading(false));
		}
	};

	const loadAwardOptions = async () => {
		setIsFetchingOptions(true);

		try {
			const results = await api.awards.getAttributes({
				attributes: START_AWARD_FROM_AWARD.searchFields,
				requiredAttributes: START_AWARD_FROM_AWARD.requiredFields,
				search: searchInput,
				projectId: fromProject ? projectId : null,
			});

			const updatedOptions = results?.content?.map((awardItem, index) => {
				const principalInvestigator =
						awardItem["principalInvestigator"] === undefined ? "" : ` -  ${awardItem["principalInvestigator"]}`;
				const projectTitle =
						awardItem["projectTitle"] === undefined ? "Untitled" : ` ${awardItem["projectTitle"]}`;
				const textString = `${awardItem.awardNumber} : ${projectTitle} ${principalInvestigator}`;

				return {
					content: textString,
					key: awardItem.awardId,
					original: textString,
					projectTitle: awardItem.projectTitle,
					text: `Result ${index + 1} of ${results?.content?.length} for ${searchInput}: ${textString}`,
					value: awardItem.awardId,
				};
			});

			const toManyResultsText = addToManyResultsText(updatedOptions, results?.totalElements);

			if(toManyResultsText) {
				updatedOptions.push(toManyResultsText);
			}
			setAwardOptions(updatedOptions);
		} finally {
			setIsFetchingOptions(false);
		}
	};

	useEffect(() => {
		setSearchField(defaults.searchField);
		setAwardOptions([]);
		setProjectOptions([]);
		if (isNonEmptyString(searchInput)) {
			if(action === actions.addToAward) {
				loadAwardOptions();
			}
			if(action === actions.addToProject) {
				loadProjectOptions();
			}
		}
	}, [ searchInput ]);

	const resetModalValues = () => {
		setAction(fromProject ? actions.addToProject : defaults.action);
		setConfirmDisabled(!fromProject);
		setVisible(defaults.visible);
		setTitleField(defaults.titleField);
		setSearchField(defaults.searchField);
		setSearchInput(defaults.searchInput);
		setOpen(true);
	};

	const refreshData = () => {
		fetchShell();
	};

	const onClose = () => {
		setOpen(false);

		const triggerButton = document.getElementById("startNewSubAwardBtn");

		if (triggerButton) {
			triggerButton.focus();
		}
	};

	const onSave = useCallback(async payload => {
		try {
			setConfirmDisabled(true);
			const response = await api.subaward.createOrCopy(payload, { notifyUponError: false });
			const subawardId = response.subawardId;
			const failedElements = response.failedFormElements;

			if(failedElements && failedElements.length !== 0) {
				const lastActiveElement = document.activeElement;
				toast(<IncompatibleWarning elements={failedElements} />, {
					type: "warning",
					autoClose: false,
					onOpen: () => {
						trapFocusInsideNotification();
					},
					onClose: () => {
						setTimeout(() => {
							lastActiveElement.focus();
						}, 0);
					}
				});
			}

			refreshData();
			onClose();

			notifySuccess(dictionary.successMessages);
			history.push(spUrl(`${SUBAWARDS_BASE_ROUTE}/${subawardId}`));
		} catch (error) {
			console.error(error);
			if (error.response?.status === ERROR_STATUS_CODE_400) {
				notifyError(dictionary.errors.notAuthorized || ERROR_NOT_AUTHORIZED);
			} else {
				notifyError(dictionary.errors.default || "Failed to copy data from form");
			}
		} finally {
			setIsLoading(false);
		}
	}, [ dictionary.errors.default, dictionary.errors.notAuthorized ]);

	const onTitleChange = event => {
		const projectTitle = event.target.value;
		setConfirmDisabled(!projectTitle || !isNonEmptyString(projectTitle.trim()) || !validateTitleLength(projectTitle));
		setTitleField(projectTitle);
	};

	const onSelectChanged = (formInstanceId, options, type) => {
		// Used to change the display text of the selected item
		const updateDisplayText = () => {
			// find the selected option and restore the original value
			// const selected = _find(options, { value: value });
			const selected = options.find(option => option.key === formInstanceId);

			if(isNonEmptyObject(selected)) {
				selected.text = selected.original;
			}

			const updatedOptions = options.map(option => option.key === selected.key ? selected : option);

			if(type === 'award') {
				setAwardOptions(updatedOptions);
			} else {
				setProjectOptions(updatedOptions);
			}
		};

		if(isNonEmptyString(formInstanceId)) {
			updateDisplayText();
			setSearchField(formInstanceId);
			setConfirmDisabled(false);
		} else {
			setSearchInput(defaults.searchInput);
			setSearchField(defaults.searchField);
			setConfirmDisabled(defaults.confirmDisabled);
		}
	};

	const onSearchChange = debounce(searchQuery => {
		setSearchInput(searchQuery);
	}, 750);

	const onSaveClicked = useCallback(() => {
		setIsLoading(true);
		if (action === actions.create) {
			onSave({ projectTitle: titleField });
		} else if (action === actions.addToProject) {
			onSave({ projectId: fromProject ? projectId : searchField });
		} else if (action === actions.addToAward) {
			onSave({ awardId: searchField });
		} else {
			console.error("Not implemented: ", action);
		}
	}, [ action, titleField, searchField ]);

	useEffect(() => {
		if (visible) {
			ReactTooltip.rebuild();
		}
	}, [ visible ]);

	return (
		<Modal
			id={MODAL_ID}
			className={styles.modal}
			closeIcon
			trigger={
				<Button
					icon="plus"
					primary
					disabled={!canCreateSubawards}
					onClick={resetModalValues}
					{...ariaControlsAttr}
					id="startNewSubAwardBtn"
					aria-haspopup="dialog"
				>
					{ fromProject ? dictionary.addNew ?? "Add A Subaward" : dictionary.startNew ?? "Start New Subaward" }
				</Button>
			}
			open={open}
			onClose={onClose}
			size="one-half"
			ariaLabel={dictionary.createNewSubaward || "Create New Subaward"}
		>
			<SemanticModal.Header className="ui header whiteOnBlue">
				<h2 id="newSubawardFormTitle">{dictionary.createNewSubaward || "Create New Subaward"}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content>
				<div className={`ui form ${styles.newSubawardForm}`} role="group" aria-labelledby="newSubawardFormTitle">
					{!fromProject && <>
						<Radio
							id={actions.create}
							name="actionGroup"
							checked={action === actions.create}
							value={actions.create}
							onChange={() => onActionChange(actions.create)}
						>
							<span dangerouslySetInnerHTML={{
								__html: dictionary.createNewProjectLabel || "<b>Create a new project</b> (create a new project and subaward at the same time)"
							}}
							/>
						</Radio>
						{visible && action === actions.create && (
							<div className={styles.dropdownWrapper}>
								<label htmlFor="newProjectTitleInput" className={styles.label}>
									{dictionary.newLabel || " Enter a title for the project"}
								</label>
								<input
									id="newProjectTitleInput"
									className={styles.modalInput}
									placeholder={dictionary.projectSearchPlaceholder || "New project title"}
									type="text"
									onChange={onTitleChange}
									value={titleField}
								/>
								{!validateTitleLength(titleField) && <span className={styles.titleLengthWarning}>
									{dictionary.errors.projectTitleLengthValidation ?? "Project title must be less than 256 characters."}
								</span>}
							</div>
						)}
					</>}

					<Radio
						id={actions.addToProject}
						name="actionGroup"
						checked={action === actions.addToProject}
						value={actions.addToProject}
						onChange={() => onActionChange(actions.addToProject)}
					>
						<span dangerouslySetInnerHTML={{
							__html:
								dictionary.addToProjectLabel || "<b>Add to existing project</b> (create a new subaward linked directly to a project)",
						}}
						/>
					</Radio>

					{visible && !fromProject && action === actions.addToProject && <div className={styles.dropdownWrapper}>
						<label htmlFor="existingProjectTitleSearch" className={styles.label}>
							{dictionary.searchProjectLabel || "Click to search by Project Number or Project Title"}
						</label>
						<div className={`${styles.dropdownWrapper} ${styles.nested}`}>
							<SearchBar
								className={`${styles.modalInput} ${isFetchingOptions ? "loading" : ""}`}
								options={projectOptions}
								onChange={(event, { value }) => onSelectChanged(value, projectOptions)}
								onSearchChange={(event, { searchQuery }) => onSearchChange(searchQuery)}
								value={searchField}
							/>
						</div>
					</div>}

					{projectHasAwards && <>
						<Radio
							id={actions.addToAward}
							name="actionGroup"
							checked={action === actions.addToAward}
							value={actions.addToAward}
							onChange={() => onActionChange(actions.addToAward)}
						>
							<span dangerouslySetInnerHTML={{
								__html:
									dictionary.addToAwardLabel || "<b>Add to award </b> (create a new subaward linked to an award)",
							}}
							/>
						</Radio>
						{action === actions.addToAward && (<div className={styles.dropdownWrapper}>
							<label htmlFor="existingProjectTitleSearch" className={styles.label}>
								{dictionary.subawardSearchLabel || "Select award"}
							</label>
							<div className={`${styles.dropdownWrapper} ${styles.nested}`}>
								<SearchBar
									className={`${styles.modalInput} ${isFetchingOptions ? "loading" : ""}`}
									options={awardOptions}
									onChange={(event, { value }) => onSelectChanged(value, awardOptions, "award")}
									onSearchChange={(event, { searchQuery }) => onSearchChange(searchQuery)}
									placeholder={ fromProject ? dictionary.subawardProjectSearchPlaceholder || "Click to search by award number" : dictionary.subawardSearchPlaceholder || "Click to search by award number, project title, or PI"}
									value={searchField}
								/>
							</div>
						</div>
						)}
					</>}
				</div>
			</SemanticModal.Content>
			<SemanticModal.Actions>
				<div className="field">
					<ButtonGroup>
						<Button secondary onClick={onClose}>
							{dictionary.cancel || "Cancel"}
						</Button>
						<Button
							className={styles.confirmButton}
							onClick={onSaveClicked}
							disabled={confirmDisabled || isLoading}
							busy={isLoading}
							loadingMessage={dictionary.creatingProjectAndSubaward || "Creating Subaward"}
							tooltip={confirmDisabled && disabledReason}
						>
							{dictionary.createSubawardButton || "Create Subaward"}
						</Button>
					</ButtonGroup>
				</div>
			</SemanticModal.Actions>
		</Modal>
	);
};

export default StartNewSubaward;
